import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import './table.scss';



const ProductionPlanTable = ({ dishListData, isCategoryAvailable }: any): JSX.Element => {
  const formatData = (data: any[]): string | null => {
    if (!data) return null;
    const sites = Array.isArray(data) ? data : (typeof data === 'object' ? Object.values(data) : []);

  const weightPerPcSum: Record<string, { quantity: number; uom: string }> = {};
  const weightSiteMap: Record<string, string[]> = {};
  const siteRemarks: string[] = [];
  const siteTags: string[] = [];

    const isNonEmpty = (v: any) => v != null && String(v).trim().length > 0;

    for (const site of sites) {
      if (!site) continue;

      const portionList = site.portionverientlist || site.portionvariantlist || site.portionList || [];
      if (!portionList || !portionList.length) {
        // no portions for this site, skip
        continue;
      }

      // collect any non-empty remarks (support `remark` and `remarks`)
      const remarksArr: any[] = Array.isArray(site.remark) ? site.remark : Array.isArray(site.remarks) ? site.remarks : (site.remark ? [site.remark] : site.remarks ? [site.remarks] : []);
      if (remarksArr && remarksArr.length) {
        const filtered = remarksArr.filter(r => isNonEmpty(r)).map(r => String(r).trim());
        if (filtered.length) {
          const codePart = site.sitecode ? ` - ${site.sitecode}` : '';
          siteRemarks.push(`${site.sitename || site.sitecafename || site.cafename || ''}${codePart}: ${filtered.join(', ')}`);
        }
      }

      // compute site-specific Pcs quantity and aggregate weights
      let siteQty = 0;
      const siteWeightCounts: Record<string, number> = {};
      for (const portion of portionList) {
        if (!portion) continue;
        const quantity = Number(portion.quantity ?? portion.qty ?? 0) || 0;
        const portionuom = portion.portionuom ?? portion.uom ?? '';
        const uomNorm = String(portionuom).toLowerCase();
        const weightperpc = portion.weightperpc ?? portion.weightPerPc ?? portion.weight ?? '';

        if (uomNorm === 'pcs' || uomNorm === 'pc') {
          siteQty += quantity;
          const key = String(weightperpc).trim();
          if (!key) continue;
          // aggregate global totals
          if (weightPerPcSum[key]) {
            weightPerPcSum[key].quantity += quantity;
          } else {
            weightPerPcSum[key] = { quantity, uom: portionuom };
          }
          // accumulate per-site counts for this weight
          siteWeightCounts[key] = (siteWeightCounts[key] || 0) + quantity;
        }
      }
      // record which sites contributed to which weight totals
      const siteLabel = site.sitename || site.sitecafename || site.cafename || site.name || '';
      if (siteLabel) {
        for (const [key, cnt] of Object.entries(siteWeightCounts)) {
          if (!weightSiteMap[key]) weightSiteMap[key] = [];
          weightSiteMap[key].push(`${siteLabel} - ${cnt}`);
        }
      }

      // add a site tag entry like SiteName-50 only when site has quantity > 0
      if (siteLabel && siteQty > 0) {
        siteTags.push(`${siteLabel} - ${siteQty}`);
      }
    }

    // Build strings that pair each weight total with the sites that contributed to it
    const weightEntries = Object.entries(weightPerPcSum).map(([weight, d]) => {
      const siteList = weightSiteMap[weight] || [];
      const sitePart = siteList.length ? ` [${siteList.join(', ')}]` : '';
      return `${d.quantity}-${weight}/${d.uom}${sitePart}`;
    });

  const parts: string[] = [];
  if (weightEntries.length) parts.push(...weightEntries);
  if (siteRemarks.length) parts.push(siteRemarks.join(', '));
  // fallback: if we have global site tags but no weight entries, include them
  if (!weightEntries.length && siteTags.length) parts.push(`[${siteTags.join(', ')}]`);

  if (parts.length === 0) return null;
  return parts.join('\n');
  };


  return (
    <div className="table-wrapper production-plan-table">
      <TableContainer component={Paper} className="table-inner" sx={{ maxHeight: 440 }}>
        <MuiTable stickyHeader>
          <TableHead>
            <TableRow>
              {isCategoryAvailable && <TableCell className='dishcategory'>
                <Typography variant="h6" className="table-title">
                  Dish Category
                </Typography>
              </TableCell>}
              <TableCell className={`${isCategoryAvailable ? 'dishname' : 'dishname without-category'}`}>
                <Typography variant="h6" className="table-title">
                  Dish Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6" className="table-title">
                  Diet Category
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6" className="table-title">
                  Total Qty
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6" className="table-title">
                  UOM
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6" className="table-title">
                  Remarks
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!!dishListData?.length ? dishListData?.map((dish: any, index: number) => {
              const remarkText = formatData(dish?.remarks);
              return (
                <TableRow key={index}>
                  {isCategoryAvailable && <TableCell className='dishcategory'>
                    {dish?.categoryname}
                  </TableCell>}
                  <TableCell className={`${isCategoryAvailable ? 'dishname' : 'dishname without-category'}`}>
                    {dish?.name}
                  </TableCell>
                  <TableCell>
                    {dish?.dietcategory}
                  </TableCell>
                  <TableCell>
                    {dish?.uom == "Kg" ? dish?.totalqty.toFixed(3) : dish?.totalqty}
                  </TableCell>
                  <TableCell>
                    {dish?.uom}
                  </TableCell>
                  <TableCell>
                    {remarkText ? <div style={{ whiteSpace: 'pre-line' }}>{remarkText}</div> : null}
                  </TableCell>
                </TableRow>
              )
            }) : <TableRow><TableCell>No dish are found</TableCell></TableRow>}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </div>
  );
};

export default ProductionPlanTable;